
import { NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxCaptchaModule } from 'ngx-captcha';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { APP_BASE_HREF } from '@angular/common';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

// Shared module (provides TranslatorPipe, SafeHTMLPipe, NoDataComponent, PrimeNG, etc.)
import { SharedModule } from './shared/shared.module';

// 3rd party modules needed by eagerly-loaded components
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { NgxAngularQueryBuilderModule } from 'ngx-angular-query-builder';
import { NgOtpInputModule } from 'ng-otp-input';
import { ConfirmationService, MessageService } from 'primeng/api';
import { RecaptchaModule, RecaptchaFormsModule } from 'ng-recaptcha';
import { RECAPTCHA_SETTINGS, RecaptchaSettings } from 'ng-recaptcha';
import { environment } from 'environment';
import { QRCodeModule } from 'angularx-qrcode';
import { YouTubePlayerModule } from '@angular/youtube-player';

// Layout (Nav, Sidenav, Footer in SharedModule)
import { AdminComponent } from './admin/admin.component';
import { AdminLayoutComponent } from './admin/layout/admin-layout/admin-layout.component';

// Home & Public Components
import { HomeComponent } from './views/home/home.component';
import { HomeSearchbarComponent } from './views/home/home-searchbar/home-searchbar.component';
import { HomeRecentComponent } from './views/home/home-recent/home-recent.component';
import { HomeEntitiesComponent } from './views/home/home-entities/home-entities.component';
import { HomeOtherpagesComponent } from './views/home/home-otherpages/home-otherpages.component';
import { TrainingRevampComponent } from './views/home/training-revamp/training-revamp.component';
import { PopupTutorialComponent } from './views/home/popup-tutorial/popup-tutorial.component';
import { PopupviewQuestionsComponent } from './views/home/popupview-questions/popupview-questions.component';

// Auth Components (SignupFormComponent moved to SharedModule)
import { LoginComponent } from './views/login/login.component';
import { ActivateAccountComponent } from './views/login/activate-account/activate-account.component';
import { EditmissingComponent } from './views/login/editmissing/editmissing.component';
import { SignupComponent } from './views/signup/signup.component';
import { ChangePasswordComponent } from './views/change-password/change-password.component';

// Category & Search
import { CategoryComponent } from './views/category/category.component';
import { CategorySearchbarComponent } from './views/category/category-searchbar/category-searchbar.component';
import { CategoryOusComponent } from './views/category/category-ous/category-ous.component';
import { ListOusComponent } from './views/category/list-ous/list-ous.component';
import { DataViewComponent } from './views/category/data-view/data-view.component';
import { SearchResultComponent } from './views/category/search-result/search-result.component';
import { AdvanceSearchComponent } from './views/category/advance-search/advance-search.component';
import { SearchViewComponent } from './views/category/search-view/search-view.component';
import { ListServicesComponent } from './views/category/list-services/list-services.component';
import { PageHeadingComponent } from './views/category/list-services/page-heading/page-heading.component';
import { ServicesViewComponent } from './views/category/list-services/services-view/services-view.component';
import { SignHistoryComponent } from './views/category/sign-history/sign-history.component';

// Single/All Data View
import { SingleDataViewComponent } from './views/single-data-view/single-data-view.component';
import { EservicepanelviewComponent } from './views/single-data-view/eservicepanelview/eservicepanelview.component';
import { AllDataViewComponent } from './views/all-data-view/all-data-view.component';
import { AllDataViewHeaderComponent } from './views/all-data-view/all-data-view-header/all-data-view-header.component';
import { AllDataViewItemsComponent } from './views/all-data-view/all-data-view-items/all-data-view-items.component';

// User Profile
import { UserProfileComponent } from './views/user-profile/user-profile.component';
import { UserStatsComponent } from './views/user-profile/user-stats/user-stats.component';

// Knowledge Library (eagerly loaded)
import { ViewComponent } from './admin/views/knowledge_library/view/view.component';
import { KLibraryAdminComponent } from './admin/views/knowledge_library/admin/klibrary_admin.component';
import { KCategoryComponent } from './admin/views/knowledge_library/k-category/k-category.component';
import { KlApprovalComponent } from './admin/views/training/kl-approval/kl-approval.component';

// Company (eagerly loaded — AddExternalUser in SharedModule)
import { CompanyComponent } from './admin/views/company/company.component';
import { AddExternalCoordinatorComponent } from './admin/views/company/add-external-coordinator/add-external-coordinator.component';
import { CoordinatorViewComponent } from './admin/views/company/coordinator-view/coordinator-view.component';
import { ViewTrainersComponent } from './admin/views/company/view-trainers/view-trainers.component';

// Products (eagerly loaded)
import { ProductsServicesComponent } from './products-services/products-services.component';
import { ProductServicesFormComponent } from './products-services/product-services-form/product-services-form.component';
import { ProductOrdersComponent } from './products-services/product-orders/product-orders.component';
import { ProductTypeComponent } from './products-services/product-type/product-type.component';
import { OrderDetailsComponent } from './products-services/order-details/order-details.component';
import { OrderListingComponent } from './products-services/order-listing/order-listing.component';
import { OrderDetailComponent } from './products-services/order-detail/order-detail.component';

// Data components (non-admin prefix routes)
import { BulkComponent } from './admin/views/data/bulk/bulk.component';
import { BulkviewComponent } from './admin/views/data/bulk/bulkview/bulkview.component';
import { ContentUpdatePageComponent } from './admin/views/data/content-update-page/content-update-page.component';

// Components for :from/* dynamic routes (AddQuestionComponent, QuestionsEditComponent, AssessmentResultModuleComponent, ReminderModalComponent moved to SharedModule)
import { SurveyTagsManagementComponent } from './admin/views/survey/survey-tags-management/survey-tags-management.component';
import { PdfBuilderComponent } from './pdf-builder/pdf-builder.component';
import { PdfComponentsComponent } from './pdf-builder/pdf-components/pdf-components.component';
import { DragWrapperComponent } from './misc/component/drag-wrapper/drag-wrapper.component';

// Misc eagerly-loaded components
import { NotificationComponent } from './notification/notification.component';
import { InvestorControllerComponent } from './investor-controller/investor-controller.component';
import { RedirectSurveyComponent } from './misc/redirect-survey-component/redirect-survey-component.component';
import { PreodicReportsComponent } from './preodic-reports/preodic-reports.component';
import { CreatePeriodicReportsComponent } from './preodic-reports/create-periodic-reports/create-periodic-reports.component';
import { ContentExpertsManagementComponent } from './preodic-reports/content-experts-management/content-experts-management.component';
import { ThemeSettingsComponent } from './theme-settings/theme-settings.component';
import { PermissionRequestsComponent } from './permission-requests/permission-requests.component';
import { PermissionRequestFormComponent } from './permission-requests/permission-request-form/permission-request-form.component';
import { PermissionRequestFlowComponent } from './permission-requests/permission-request-flow/permission-request-flow.component';
import { AuthInterceptorService } from './services/auth-interceptor.service';
import { VideosComponent } from './views/static-pages/videos/videos.component';
import { UserGuideZatcaComponent } from './views/static-pages/user-guides/user-guide-zatca.component';

// Standalone components (imported, not declared)
import { QuestionTemplateComponentStandAlone } from './admin/views/survey/question-template-standalone/question-template-standalone.component';

@NgModule({
  declarations: [
    // App shell
    AppComponent,
    AdminComponent,
    AdminLayoutComponent,

    // Home & Public
    HomeComponent,
    HomeSearchbarComponent,
    HomeRecentComponent,
    HomeEntitiesComponent,
    HomeOtherpagesComponent,
    TrainingRevampComponent,
    PopupTutorialComponent,
    PopupviewQuestionsComponent,

    // Auth
    LoginComponent,
    ActivateAccountComponent,
    EditmissingComponent,
    SignupComponent,
    ChangePasswordComponent,

    // Category & Search
    CategoryComponent,
    CategorySearchbarComponent,
    CategoryOusComponent,
    ListOusComponent,
    DataViewComponent,
    SearchResultComponent,
    AdvanceSearchComponent,
    SearchViewComponent,
    ListServicesComponent,
    PageHeadingComponent,
    ServicesViewComponent,
    SignHistoryComponent,

    // Data Views
    SingleDataViewComponent,
    EservicepanelviewComponent,
    AllDataViewComponent,
    AllDataViewHeaderComponent,
    AllDataViewItemsComponent,

    // User Profile
    UserProfileComponent,
    UserStatsComponent,

    // Knowledge Library
    ViewComponent,
    KLibraryAdminComponent,
    KCategoryComponent,
    KlApprovalComponent,

    // Company
    CompanyComponent,
    AddExternalCoordinatorComponent,
    CoordinatorViewComponent,
    ViewTrainersComponent,

    // Products
    ProductsServicesComponent,
    ProductServicesFormComponent,
    ProductOrdersComponent,
    ProductTypeComponent,
    OrderDetailsComponent,
    OrderListingComponent,
    OrderDetailComponent,

    // Data
    BulkComponent,
    BulkviewComponent,
    ContentUpdatePageComponent,

    // :from/* dynamic route components
    SurveyTagsManagementComponent,
    PdfBuilderComponent,
    PdfComponentsComponent,
    DragWrapperComponent,

    // Misc
    NotificationComponent,
    InvestorControllerComponent,
    RedirectSurveyComponent,
    PreodicReportsComponent,
    ContentExpertsManagementComponent,
    CreatePeriodicReportsComponent,
    ThemeSettingsComponent,
    PermissionRequestsComponent,
    PermissionRequestFormComponent,
    PermissionRequestFlowComponent,
    VideosComponent,
    UserGuideZatcaComponent,
  ],
  imports: [
    // Angular core
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    CommonModule,
    // Shared module (pipes, directives, shared components, PrimeNG, Material, Forms, NgxPrint)
    SharedModule,
    // App routing
    AppRoutingModule,
    // 3rd party modules needed by eagerly-loaded components
    NgxCaptchaModule,
    NgxDocViewerModule,
    NgxAngularQueryBuilderModule,
    NgOtpInputModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    QRCodeModule,
    YouTubePlayerModule,
    // Standalone components
    QuestionTemplateComponentStandAlone,
  ],
  providers: [
    MessageService,
    ConfirmationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: environment.recaptchaSiteKey,
      } as RecaptchaSettings,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
